import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  FormCard,
  FormTextField,
  BackButton,
  NextButton,
} from './UI';
import { CreateDonationRequest } from '@/types';

type DonationFormData = {
  amount: number;
  paymentMethod: 'CASH' | 'CARD' | 'BANK_TRANSFER' | 'CHECK' | 'OTHER';
  notes?: string;
};

interface DonationDetailsStepProps {
  initialData?: Partial<CreateDonationRequest>;
  onDataChange: (data: Partial<CreateDonationRequest>) => void;
  onNext: () => void;
  onBack: () => void;
  showNavigationButtons?: boolean;
}

const schema = yup.object({
  amount: yup
    .number()
    .positive('L\'importo deve essere maggiore di zero')
    .required('L\'importo è obbligatorio')
    .max(10000, 'L\'importo non può superare €10.000'),
  paymentMethod: yup
    .string()
    .oneOf(['CASH', 'CARD', 'BANK_TRANSFER', 'CHECK', 'OTHER'], 'Metodo di pagamento non valido')
    .required('Il metodo di pagamento è obbligatorio'),
  notes: yup.string().optional(),
});

const paymentMethods = [
  { value: 'CASH', label: 'Contanti' },
  { value: 'CARD', label: 'Carta di Credito/Debito' },
  { value: 'BANK_TRANSFER', label: 'Bonifico Bancario' },
  { value: 'CHECK', label: 'Assegno' },
  { value: 'OTHER', label: 'Altro' },
];

const suggestedAmounts = [10, 25, 50, 100, 250, 500];

export const DonationDetailsStep: React.FC<DonationDetailsStepProps> = ({
  initialData,
  onDataChange,
  onNext,
  onBack,
  showNavigationButtons = true,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<DonationFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      amount: initialData?.amount || 0,
      paymentMethod: initialData?.paymentMethod || 'CASH',
      notes: initialData?.notes || '',
    },
    mode: 'onChange',
  });

  const watchedValues = watch();

  // Aggiorna i dati quando cambiano
  React.useEffect(() => {
    if (isValid) {
      // Assicurati che l'importo sia sempre un numero
      const cleanData = {
        ...watchedValues,
        amount: typeof watchedValues.amount === 'string' ? parseFloat(watchedValues.amount) : watchedValues.amount
      };
      onDataChange(cleanData as Partial<CreateDonationRequest>);
    }
  }, [watchedValues, isValid, onDataChange]);

  const handleSuggestedAmount = (amount: number) => {
    setValue('amount', amount, { shouldValidate: true });
  };

  const onSubmit = async (data: DonationFormData) => {
    setIsSubmitting(true);
    try {
      // Assicurati che l'amount sia un numero
      const cleanData = {
        ...data,
        amount: data.amount ? Number(data.amount) : undefined,
      };
      onDataChange(cleanData as Partial<CreateDonationRequest>);
      onNext();
    } catch (error) {
      console.error('Error saving donation data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dettagli Donazione
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Inserisci l'importo della donazione e il metodo di pagamento utilizzato.
      </Typography>

      <FormCard
        title="Importo Donazione"
        subtitle="Specifica l'importo donato"
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Importo (€) *"
                    type="number"
                    error={errors.amount?.message}
                    required
                    value={field.value?.toString() || '0'}
                  />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Importi Suggeriti
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestedAmounts.map((amount) => (
                <Box
                  key={amount}
                  onClick={() => handleSuggestedAmount(amount)}
                  sx={{
                    px: 2,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: watchedValues.amount === amount ? 'primary.main' : 'transparent',
                    color: watchedValues.amount === amount ? 'white' : 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'white',
                    },
                  }}
                >
                  €{amount}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </FormCard>

      <FormCard
        title="Metodo di Pagamento"
        subtitle="Come è stata effettuata la donazione"
        sx={{ mt: 3 }}
      >
        <Controller
          name="paymentMethod"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.paymentMethod}>
              <InputLabel>Metodo di Pagamento *</InputLabel>
              <Select
                {...field}
                label="Metodo di Pagamento *"
                sx={{ borderRadius: 2 }}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.paymentMethod && (
                <FormHelperText>{errors.paymentMethod.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />

        {watchedValues.paymentMethod === 'CARD' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Per le donazioni con carta, assicurati di scattare una foto dello scontrino 
              o del terminale di pagamento nel prossimo passaggio.
            </Typography>
          </Alert>
        )}

        {watchedValues.paymentMethod === 'BANK_TRANSFER' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Per i bonifici bancari, scatta una foto della ricevuta di bonifico 
              o del documento di trasferimento.
            </Typography>
          </Alert>
        )}

        {watchedValues.paymentMethod === 'CHECK' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Per gli assegni, scatta una foto dell'assegno (fronte e retro) 
              nel prossimo passaggio.
            </Typography>
          </Alert>
        )}
      </FormCard>

      <FormCard
        title="Note Aggiuntive"
        subtitle="Informazioni aggiuntive sulla donazione (opzionale)"
        sx={{ mt: 3 }}
      >
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Note"
                    multiline
                    rows={4}
                    placeholder="Aggiungi eventuali note sulla donazione..."
                    error={errors.notes?.message}
                    value={field.value || ''}
                  />
          )}
        />
      </FormCard>

      {showNavigationButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <BackButton onClick={onBack} />
          <NextButton
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Box>
      )}
    </Box>
  );
};
